'use client';

import { TierLevelEnum } from '@wwe-portal/entities/roster.type';
import { FC } from 'react';

import tierTagLongA from '@wwe-portal/ui/public/assets/images/roster/tier/tier-tag-long-a.webp';
import tierTagLongB from '@wwe-portal/ui/public/assets/images/roster/tier/tier-tag-long-b.webp';
import tierTagLongC from '@wwe-portal/ui/public/assets/images/roster/tier/tier-tag-long-c.webp';
import tierTagLongS from '@wwe-portal/ui/public/assets/images/roster/tier/tier-tag-long-s.webp';
import tierTagLongSPlus from '@wwe-portal/ui/public/assets/images/roster/tier/tier-tag-long-splus.webp';

import tierTagShortA from '@wwe-portal/ui/public/assets/images/roster/tier/tier-tag-short-a.webp';
import tierTagShortB from '@wwe-portal/ui/public/assets/images/roster/tier/tier-tag-short-b.webp';
import tierTagShortC from '@wwe-portal/ui/public/assets/images/roster/tier/tier-tag-short-c.webp';
import tierTagShortS from '@wwe-portal/ui/public/assets/images/roster/tier/tier-tag-short-s.webp';
import tierTagShortSPlus from '@wwe-portal/ui/public/assets/images/roster/tier/tier-tag-short-splus.webp';
import { cn } from '@wwe-portal/ui/lib/lib';
import { AspectRatio } from '@wwe-portal/ui/components/aspect-ratio';
import { TextSkeleton } from '@wwe-portal/ui/components/skeleton';

import Image from 'next/image';
import { Textfit } from 'react-textfit';

const tierTagLongMap = {
  [TierLevelEnum.A]: tierTagLongA,
  [TierLevelEnum.B]: tierTagLongB,
  [TierLevelEnum.C]: tierTagLongC,
  [TierLevelEnum.S]: tierTagLongS,
  [TierLevelEnum.SPlus]: tierTagLongSPlus,
} as const;

const tierTagShortMap = {
  [TierLevelEnum.A]: tierTagShortA,
  [TierLevelEnum.B]: tierTagShortB,
  [TierLevelEnum.C]: tierTagShortC,
  [TierLevelEnum.S]: tierTagShortS,
  [TierLevelEnum.SPlus]: tierTagShortSPlus,
} as const;

const tierTagMap = {
  long: tierTagLongMap,
  short: tierTagShortMap,
} as const;

const isTier = (tier: string): tier is TierLevelEnum => {
  return Object.values(TierLevelEnum).includes(tier as TierLevelEnum);
};

const parseTier = (tier: string) => {
  if (isTier(tier)) {
    return tier;
  }
  return TierLevelEnum.Other;
};

const isSize = (size: string): size is 'long' | 'short' => {
  return size === 'long' || size === 'short';
};

const parseSize = (size: string) => {
  if (isSize(size)) {
    return size;
  }
  return 'long';
};

const RosterTierLayout: FC<{
  size: 'long' | 'short';
  children: React.ReactNode;
  ratio?: number;
}> = ({ size, children, ratio }) => {
  return (
    <AspectRatio
      className="relative w-full"
      ratio={ratio ?? (size === 'long' ? 150 / 20 : 90 / 20)}
    >
      {children}
    </AspectRatio>
  );
};

export const RosterTier: FC<{
  tier: string;
  size: 'long' | 'short';
  ratio?: number;
}> = ({ tier, size, ratio }) => {
  return (
    <RosterTierLayout size={size} ratio={ratio}>
      <div className="absolute inset-0">
        <Image
          src={tierTagMap[parseSize(size)][parseTier(tier)]}
          alt={tier}
          className="absolute inset-0 h-full w-full object-contain"
          width={93}
          height={21}
        />
      </div>
      <div className="absolute inset-0 inset-x-1/4 top-[10%] flex items-center">
        <Textfit
          mode="single"
          max={14}
          className={cn(
            'drop-shadow-text flex max-h-full w-full justify-center stroke-1 text-center font-semibold italic leading-none text-white',
            {
              uppercase: size === 'long',
            }
          )}
          forceSingleModeWidth={false}
        >
          {tier} Tier
        </Textfit>
      </div>
    </RosterTierLayout>
  );
};

export const RosterTierSkeleton: FC<{ size: 'long' | 'short' }> = ({
  size,
}) => {
  return (
    <RosterTierLayout size={size}>
      <div className="flex h-full w-full justify-center">
        <TextSkeleton
          lineHeight={14}
          fontSize={12}
          className="mt-1 h-full w-4/5"
        />
      </div>
    </RosterTierLayout>
  );
};
